import { Component, OnInit, OnDestroy, Input, ElementRef, ViewChild, Renderer2, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { fromEvent, Subject, interval, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IContextService, LogService, DomainContext } from '@pershing/pershing-core';

@Component({
    selector: 'pershing-iframe',
    template: `
        <div #IFrameAnchor>
            <ng-container *ngIf="isLoading" class="center-align" style="margin: auto;">
                <mat-spinner [diameter]="40"></mat-spinner>
            </ng-container>
        </div>
        <form #iForm name="per-frame" method="post" [action]="_src | pershingSafe: 'resourceUrl'" [target]="domID"></form>
    `,
    styles: [`
        .per-iframe {
            overflow: hidden;
            width: 100%;
            margin: auto;
            border: none;
        }
    `]
})

export class PershingIFrameComponent implements OnDestroy, OnInit, AfterViewInit {

    @Input() options: any = {};
    @Input() postMessageEventEmitter: EventEmitter<any>;
    @Output() postMessageEventListener = new EventEmitter();
    @ViewChild('iForm', { static: false }) iframeForm: ElementRef;
    @ViewChild('IFrameAnchor', { static: false }) iframeAnchor: ElementRef;

    private IFrameElement: any = {};
    private $unsubscribe = new Subject();
    private DEFAULT_HEIGHT: string = '1000px';
    domID: string = Math.random().toString().replace('.', '');
    isLoading: boolean = true;
    _src: string = null;

    private initialContextPublishComplete: boolean = false;
    postMessageSubscription: Subscription;

    @Input()
    set src(newSrc) {
        let triggerRefresh = (this._src !== null);
        this._src = newSrc;
        if (triggerRefresh) {
            this.iframeForm.nativeElement.action = this._src;
            this.iframeForm.nativeElement.submit();
        }
    }

    constructor(private renderer: Renderer2, private contextService: IContextService,
          private logService: LogService) {}

    ngOnInit() {
        // with angular elements @input values will be packed as string,
        // so having the below check to parse it back as object
        if (this.options && typeof this.options === 'string') {
            this.options = JSON.parse(this.options);
        }
        if (this.options.params) {
            this._src += this.serialize(this.options.params);
        }
        if (this.postMessageEventEmitter) {
            this.postMessageSubscription = this.postMessageEventEmitter
                .subscribe((msg) => {
                    console.log(msg);
                    this.IFrameElement.contentWindow.postMessage(msg, '*');
                });
        }
    }

    ngAfterViewInit() {
        const iframeAnchorEle = this.iframeAnchor.nativeElement;
        const iframeFormEle = this.iframeForm.nativeElement;

        this.generateIframeElement(iframeAnchorEle);

        // Handle context passing to iframe destination url
        let currentContext = this.options.context;
        // check domains are properly configured
        if (currentContext) {
            if (currentContext.supportedDomains &&  currentContext.supportedDomains.length > 0) {
                this.contextService.selectedDomainContext$.subscribe((domainContext: DomainContext<any>) => {
                    this.packContextAndPublish(currentContext, domainContext);
                });
            } else {
                this.logService.error('PershingIFrameComponent - ngAfterViewInit - context not configured properly');
                this.initialContextPublishComplete = true;
                iframeFormEle.submit();
            }
        }

        // if loadOnContext false or not available, then do not wait for context to be available before publishing url to iframe
        // check additionally - already iframe published during context subscription
        if (typeof (this.options.loadOnContext) === 'undefined' ||
             ( !this.options.loadOnContext && !this.initialContextPublishComplete)) {
            iframeFormEle.submit();
        }

        fromEvent(this.IFrameElement, 'load')
            .pipe(takeUntil(this.$unsubscribe))
            .subscribe((e) => {
                this.isLoading = false;
                this.IFrameElement.style.display = 'block';
                this.subscribeStylingEvents();
                this.subscribeCustomEvents();
                this.subscribePostMessageEvents();
            });
    }

    subscribeStylingEvents() {
        if (!this.options.style || (this.options.style && !this.options.style.height)) {
            try {
                // Initialize iframe height to child window
                let windowHeight = this.getContentHeight(this.IFrameElement.contentWindow.document);
                this.IFrameElement.style.height = windowHeight;
                // Listen to resize event on child window
                fromEvent(this.IFrameElement.contentWindow, 'resize')
                    .pipe(takeUntil(this.$unsubscribe))
                    .subscribe((e) => {
                        this.resize();
                    });
                // Resize Listener
                interval(1000)
                    .pipe(takeUntil(this.$unsubscribe))
                    .subscribe((e) => {
                        this.resize();
                    });
            // Catch CORS Error
            } catch (error) {
                this.logService.log(`PershingIframeComponent - subscribeStylingEvents() - Event listeners and/or styles and resize
                                         listener could not be added due to a cross-origin frame error.`);
                this.logService.warn(error);
            }
        }
    }

    resize() {
        try {
            let windowHeight = this.getContentHeight(this.IFrameElement.contentWindow.document);
            this.IFrameElement.style.height = windowHeight;
        } catch(error) {
            this.logService.log('PershingIframeComponent - resize() - cross origin - iframe resize to default height');
            this.logService.warn(error);
            this.IFrameElement.style.height = this.DEFAULT_HEIGHT;
            this.$unsubscribe.next();
            this.$unsubscribe.complete();
        }
    }

    getContentHeight(document) {
        let body = document.body.scrollHeight;
        let htmlTag = document.documentElement.scrollHeight;
        return (htmlTag > body) ? htmlTag + 'px' : body + 'px';
    }

    subscribeCustomEvents() {
        try {
            // Listen to navigate event on child document, and propagate
            fromEvent(this.IFrameElement.contentWindow.document, 'portal.gotoview')
                .pipe(takeUntil(this.$unsubscribe))
                .subscribe((event: Event) => {
                    document.dispatchEvent(new CustomEvent(event.type, event));
                });
        } catch(error) {
            this.logService.log(`PershingIframeComponent - subscribeCustomEvents() - Event listeners and/or styles and resize listener
                                     could not be added due to a cross-origin frame error.`);
            this.logService.warn(error);
        }
    }

    subscribePostMessageEvents() {
        if (this.postMessageEventListener.observers.length > 0) {
            // Listen to windows postMessage Event
            fromEvent(window, 'message')
                .pipe(takeUntil(this.$unsubscribe))
                .subscribe(function (event: any) {
                    // if (event.source && event.source.name === this.domID) {
                    let obj = event.data;
                    if (typeof event.data === 'string') {
                        obj = JSON.parse(event.data);
                    }
                    console.log(`Received ${obj} from ${event.origin}`);
                    this.postMessageEventListener.emit(obj);
                    // }
                }.bind(this));
        }
    }

    generateIframeElement(anchor) {
        this.IFrameElement = this.renderer.createElement('iframe');
        this.IFrameElement.name = this.domID;
        this.IFrameElement.style.display = 'none';
        for (let key in this.options.style) {
            if (this.options.style[key]) {
                this.IFrameElement.style[key] = this.options.style[key];
            }
        }
        this.renderer.addClass(this.IFrameElement, 'per-iframe');
        this.renderer.insertBefore(anchor.parentNode, this.IFrameElement, anchor);
    }

    serialize( obj ) {
        // Adds Params to url
        return '?' +
            Object.keys(obj).reduce((a, k) => {
                    let paramValue = (typeof obj[k] === 'object') ? JSON.stringify(obj[k]) : obj[k];
                    // replace the whole value with blank when serialize method gets $ in the param object
                    //     $ will be present in the params which needs to be populated with values from context object
                    //     sample param from site-config -> "accountId": "${ACCOUNT|accountNumber}",
                    if (paramValue.indexOf('$') >= 0) {
                        paramValue = '';
                    }
                    a.push( k + '=' + encodeURIComponent(paramValue));
                    return a;
            }, []).join('&');
    }

    packContextAndPublish(currentContext, domainContext) {
        // supported contexts for the page are read from site config params input
        let supportedDomains = currentContext.supportedDomains;

        // check for incoming context matches supported domains
        if (domainContext != null && domainContext.value != null &&
                             supportedDomains.indexOf(domainContext.domain)) {
            // let params = currentContext.params;
            let params = Object.assign({}, this.options.params);

            // context params are configured in site-config in below way under the params section,
            // "<param name for target app>" : "${<domain>|<field name from context model>}"
            //      sample - "accountId": "${ACCOUNT|accountNumber}",
            //              blank spaces not allowed in above value
            // below code identifies context params with above structure and populate the values
            //      from the domain context object
            Object.keys(params).forEach(key => {
                if (params[key].indexOf('$') >= 0) {
                    let contextParam = params[key];
                    let contextParamArray = contextParam.slice(2, contextParam.length-1).split('|');
                    if (contextParamArray != null && contextParamArray.length > 0 &&
                                 contextParamArray[0] === domainContext.domain.toUpperCase()) {

                            let contextValue = domainContext.value;
                            params[key] = contextValue[contextParamArray[1]];
                    }
                }
            });

            // get domain from the input src+params
            let url = (this._src != null && this._src.indexOf('?') != -1) ? (this._src.split('?') [0]) : this._src;
            let urlWithContextParams = url + this.serialize(params);
            this.iframeForm.nativeElement.action = urlWithContextParams; // set the context params in the url
            this.initialContextPublishComplete = true;
            this.iframeForm.nativeElement.submit();
        } else {
            this.logService.error(`PershingIFrameComponent - packContextAndPublish - Either domain context null or current context
                                                 does not match the configured context`);
            this.initialContextPublishComplete = true;
            this.iframeForm.nativeElement.submit();
        }
    }

    ngOnDestroy() {
        if (this.postMessageSubscription) {
            this.postMessageSubscription.unsubscribe();
        }
        this.$unsubscribe.next();
        this.$unsubscribe.complete();
    }

}
